import React, { useEffect, useCallback } from 'react';
import {
    FileText, Download, ChevronLeft, ChevronRight,
    Loader2, AlertCircle, Car,
} from 'lucide-react';
import { useAppDispatch, useAppSelector } from '../store/hooks';
import {
    fetchFleetInvoices,
    downloadFleetInvoicePdf,
    clearFleetInvoiceErrors,
    selectFleetInvoices,
    selectFleetInvoicesLoading,
    selectFleetInvoicesError,
    selectFleetInvoicesPage,
    selectFleetInvoicesTotalPages,
    selectFleetInvoicesTotalElements,
    selectFleetInvoiceDownloadingUuid,
    selectFleetInvoiceDownloadError,
} from '../store/slices/fleetInvoiceSlice';
import Layout from './Layout';
import { formatAud } from '../utils/money';

const PAGE_SIZE = 20;

// ============================================
// Formatting
// ============================================

// Customer-facing: a missing amount is an em dash, not $0.00 — see utils/money.js.
const money = formatAud;

const shortDate = (iso) =>
    !iso
        ? '—'
        : new Date(iso).toLocaleDateString('en-AU', {
            day: 'numeric', month: 'short', year: 'numeric',
        });

const STATUS_STYLES = {
    DRAFT:          'bg-gray-100 text-gray-600 border-gray-200',
    SENT:           'bg-blue-50 text-blue-700 border-blue-200',
    PARTIALLY_PAID: 'bg-amber-50 text-amber-700 border-amber-200',
    TRADE_CREDIT:   'bg-amber-50 text-amber-700 border-amber-200',
    PAID:           'bg-emerald-50 text-emerald-700 border-emerald-200',
    RECONCILED:     'bg-emerald-50 text-emerald-700 border-emerald-200',
    OVERDUE:        'bg-red-50 text-red-700 border-red-200',
    VOID:           'bg-gray-100 text-gray-400 border-gray-200',
};

const statusLabel = (s) =>
    (s || '').split('_').map((w) => w.charAt(0) + w.slice(1).toLowerCase()).join(' ');

// ============================================
// Fleet invoices page
// ============================================

const FleetInvoicesPage = () => {
    const dispatch = useAppDispatch();

    const invoices = useAppSelector(selectFleetInvoices);
    const loading = useAppSelector(selectFleetInvoicesLoading);
    const error = useAppSelector(selectFleetInvoicesError);
    const page = useAppSelector(selectFleetInvoicesPage);
    const totalPages = useAppSelector(selectFleetInvoicesTotalPages);
    const totalElements = useAppSelector(selectFleetInvoicesTotalElements);
    const downloadingUuid = useAppSelector(selectFleetInvoiceDownloadingUuid);
    const downloadError = useAppSelector(selectFleetInvoiceDownloadError);

    const load = useCallback((targetPage = 0) => {
        dispatch(fetchFleetInvoices({ page: targetPage, size: PAGE_SIZE }));
    }, [dispatch]);

    useEffect(() => {
        load(0);
    }, [load]);

    const goToPage = (target) => {
        if (target < 0 || target >= totalPages) return;
        load(target);
    };

    const handleDownload = (inv) => {
        dispatch(clearFleetInvoiceErrors());
        dispatch(downloadFleetInvoicePdf({
            uuid: inv.uuid,
            invoiceNumber: inv.invoiceNumber,
        }));
    };

    return (
        <Layout>
            <div className="px-6 py-8">
                {/* Header */}
                <div className="flex items-center gap-3 mb-6">
                    <div className="w-11 h-11 bg-emerald-50 rounded-xl flex items-center justify-center border border-emerald-100">
                        <FileText size={22} className="text-emerald-600" />
                    </div>
                    <div>
                        <h1 className="text-2xl font-display font-bold text-gray-900">My Invoices</h1>
                        <p className="text-sm text-gray-500 mt-0.5">
                            Invoices for work done on your fleet.
                        </p>
                    </div>
                </div>

                {downloadError && (
                    <div className="mb-4 flex items-start gap-2 p-3 bg-red-50 border border-red-100 rounded-xl">
                        <AlertCircle size={16} className="text-red-500 flex-shrink-0 mt-0.5" />
                        <p className="text-sm text-red-600">{downloadError}</p>
                    </div>
                )}

                {/* Table card */}
                <div className="bg-white rounded-2xl border border-gray-200 overflow-hidden">
                    {loading ? (
                        <div className="py-16 flex flex-col items-center justify-center">
                            <Loader2 size={28} className="text-emerald-500 animate-spin" />
                            <p className="mt-3 text-sm text-gray-500">Loading your invoices…</p>
                        </div>
                    ) : error ? (
                        <div className="py-16 flex flex-col items-center justify-center px-6 text-center">
                            <div className="w-12 h-12 bg-red-50 rounded-full flex items-center justify-center mb-3">
                                <AlertCircle size={22} className="text-red-500" />
                            </div>
                            <p className="text-sm text-gray-900 font-medium">Couldn't load invoices</p>
                            <p className="text-sm text-gray-500 mt-1">{error}</p>
                            <button
                                onClick={() => load(0)}
                                className="mt-4 px-4 py-2 bg-gray-900 text-white rounded-xl text-sm font-medium hover:bg-gray-800 transition-colors cursor-pointer border-none"
                            >
                                Try again
                            </button>
                        </div>
                    ) : invoices.length === 0 ? (
                        <div className="py-16 flex flex-col items-center justify-center px-6 text-center">
                            <div className="w-12 h-12 bg-gray-100 rounded-full flex items-center justify-center mb-3">
                                <FileText size={22} className="text-gray-400" />
                            </div>
                            <p className="text-sm text-gray-900 font-medium">No invoices yet</p>
                            <p className="text-sm text-gray-500 mt-1">
                                Invoices will appear here once your vehicles have been booked in.
                            </p>
                        </div>
                    ) : (
                        <>
                            {/* Desktop table */}
                            <div className="hidden md:block overflow-x-auto">
                                <table className="w-full">
                                    <thead>
                                    <tr className="border-b border-gray-100">
                                        <th className="text-left text-xs font-semibold text-gray-500 uppercase tracking-wider px-6 py-3">Invoice</th>
                                        <th className="text-left text-xs font-semibold text-gray-500 uppercase tracking-wider px-6 py-3">Vehicle</th>
                                        <th className="text-left text-xs font-semibold text-gray-500 uppercase tracking-wider px-6 py-3">Issued</th>
                                        <th className="text-left text-xs font-semibold text-gray-500 uppercase tracking-wider px-6 py-3">Due</th>
                                        <th className="text-right text-xs font-semibold text-gray-500 uppercase tracking-wider px-6 py-3">Total</th>
                                        <th className="text-right text-xs font-semibold text-gray-500 uppercase tracking-wider px-6 py-3">Paid</th>
                                        <th className="text-right text-xs font-semibold text-gray-500 uppercase tracking-wider px-6 py-3">Balance</th>
                                        <th className="text-left text-xs font-semibold text-gray-500 uppercase tracking-wider px-6 py-3">Status</th>
                                        <th className="px-6 py-3 w-12" />
                                    </tr>
                                    </thead>
                                    <tbody className="divide-y divide-gray-50">
                                    {invoices.map((inv) => (
                                        <tr key={inv.uuid} className="hover:bg-gray-50 transition-colors">
                                            <td className="px-6 py-4">
                                                <p className="text-sm font-medium text-gray-900">{inv.invoiceNumber}</p>
                                                {inv.bookingCode && (
                                                    <p className="text-xs text-gray-400 font-mono mt-0.5">
                                                        {inv.bookingCode}
                                                    </p>
                                                )}
                                            </td>
                                            <td className="px-6 py-4">
                                                {inv.vehicleRego ? (
                                                    <span className="inline-flex items-center gap-1.5 text-sm text-gray-600">
                                                            <Car size={14} className="text-gray-400" />
                                                        {inv.vehicleRego}
                                                        </span>
                                                ) : (
                                                    <span className="text-gray-400">—</span>
                                                )}
                                            </td>
                                            <td className="px-6 py-4 text-sm text-gray-600">
                                                {shortDate(inv.issueDate)}
                                            </td>
                                            <td className="px-6 py-4 text-sm text-gray-600">
                                                {shortDate(inv.dueDate)}
                                            </td>
                                            <td className="px-6 py-4 text-sm text-gray-900 text-right font-medium">
                                                {money(inv.totalAmount)}
                                            </td>
                                            <td className="px-6 py-4 text-sm text-gray-600 text-right">
                                                {money(inv.amountPaid)}
                                            </td>
                                            <td className="px-6 py-4 text-sm text-right font-medium text-gray-900">
                                                {money(inv.balanceDue)}
                                            </td>
                                            <td className="px-6 py-4">
                                                    <span className={`inline-flex items-center px-2 py-0.5 rounded-lg text-xs font-medium border ${STATUS_STYLES[inv.status] || STATUS_STYLES.DRAFT}`}>
                                                        {statusLabel(inv.status)}
                                                    </span>
                                            </td>
                                            <td className="px-6 py-4 text-right">
                                                {/*
                                                      Drafts have no PDF — it's generated when the
                                                      workshop issues the invoice. hasPdf comes from
                                                      the backend, so the button only appears when a
                                                      download would actually succeed.
                                                    */}
                                                {inv.hasPdf && (
                                                    <button
                                                        onClick={() => handleDownload(inv)}
                                                        disabled={downloadingUuid === inv.uuid}
                                                        title="Download PDF"
                                                        className="p-2 text-gray-400 hover:text-emerald-600 hover:bg-emerald-50 rounded-lg transition-colors bg-transparent border-none cursor-pointer disabled:opacity-50"
                                                    >
                                                        {downloadingUuid === inv.uuid
                                                            ? <Loader2 size={16} className="animate-spin" />
                                                            : <Download size={16} />}
                                                    </button>
                                                )}
                                            </td>
                                        </tr>
                                    ))}
                                    </tbody>
                                </table>
                            </div>

                            {/* Mobile cards */}
                            <div className="md:hidden divide-y divide-gray-50">
                                {invoices.map((inv) => (
                                    <div key={inv.uuid} className="px-4 py-4">
                                        <div className="flex items-start justify-between gap-3">
                                            <div className="min-w-0">
                                                <div className="flex items-center gap-2 flex-wrap">
                                                    <p className="text-sm font-medium text-gray-900">
                                                        {inv.invoiceNumber}
                                                    </p>
                                                    <span className={`inline-flex items-center px-2 py-0.5 rounded-lg text-xs font-medium border ${STATUS_STYLES[inv.status] || STATUS_STYLES.DRAFT}`}>
                                                        {statusLabel(inv.status)}
                                                    </span>
                                                </div>

                                                {inv.vehicleRego && (
                                                    <p className="text-sm text-gray-500 mt-1.5 flex items-center gap-1.5">
                                                        <Car size={13} className="text-gray-400" />
                                                        {inv.vehicleRego}
                                                    </p>
                                                )}

                                                <p className="text-xs text-gray-500 mt-1">
                                                    Issued {shortDate(inv.issueDate)} · Due {shortDate(inv.dueDate)}
                                                </p>
                                            </div>

                                            {inv.hasPdf && (
                                                <button
                                                    onClick={() => handleDownload(inv)}
                                                    disabled={downloadingUuid === inv.uuid}
                                                    className="p-2 text-gray-400 hover:text-emerald-600 hover:bg-emerald-50 rounded-lg transition-colors bg-transparent border-none cursor-pointer disabled:opacity-50 flex-shrink-0"
                                                >
                                                    {downloadingUuid === inv.uuid
                                                        ? <Loader2 size={16} className="animate-spin" />
                                                        : <Download size={16} />}
                                                </button>
                                            )}
                                        </div>

                                        <div className="flex items-center gap-4 mt-3 pt-3 border-t border-gray-50">
                                            <div>
                                                <p className="text-[10px] text-gray-400 uppercase tracking-wider">Total</p>
                                                <p className="text-sm font-medium text-gray-900">{money(inv.totalAmount)}</p>
                                            </div>
                                            <div>
                                                <p className="text-[10px] text-gray-400 uppercase tracking-wider">Paid</p>
                                                <p className="text-sm text-gray-600">{money(inv.amountPaid)}</p>
                                            </div>
                                            <div>
                                                <p className="text-[10px] text-gray-400 uppercase tracking-wider">Balance</p>
                                                <p className="text-sm font-medium text-gray-900">{money(inv.balanceDue)}</p>
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>

                            {/* Pagination */}
                            <div className="flex items-center justify-between px-6 py-4 border-t border-gray-100">
                                <p className="text-sm text-gray-500">
                                    {totalElements} {totalElements === 1 ? 'invoice' : 'invoices'}
                                </p>
                                {totalPages > 1 && (
                                    <div className="flex items-center gap-2">
                                        <button
                                            onClick={() => goToPage(page - 1)}
                                            disabled={page <= 0}
                                            className="p-2 rounded-lg border border-gray-200 text-gray-600 hover:bg-gray-100 transition-colors bg-transparent cursor-pointer disabled:opacity-40 disabled:cursor-not-allowed"
                                        >
                                            <ChevronLeft size={16} />
                                        </button>
                                        <span className="text-sm text-gray-600 px-2">
                                            Page {page + 1} of {totalPages}
                                        </span>
                                        <button
                                            onClick={() => goToPage(page + 1)}
                                            disabled={page >= totalPages - 1}
                                            className="p-2 rounded-lg border border-gray-200 text-gray-600 hover:bg-gray-100 transition-colors bg-transparent cursor-pointer disabled:opacity-40 disabled:cursor-not-allowed"
                                        >
                                            <ChevronRight size={16} />
                                        </button>
                                    </div>
                                )}
                            </div>
                        </>
                    )}
                </div>
            </div>
        </Layout>
    );
};

export default FleetInvoicesPage;